/**
 * Pelea actual — la vista que ve TODO el mundo, incluido el apostador sin permisos.
 * Se actualiza por SSE (con fallback a polling) y muestra el estado del pozo en vivo.
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FightMetrics, StreamEvent } from '@thepit/contracts';
import { useCurrentFight, queryKeys } from '../lib/queries.js';
import { useFightStream } from '../lib/useStream.js';
import { useT } from '../lib/i18n.js';
import { formatMoney, formatOdds, formatPercentBp, formatRelative } from '../lib/format.js';
import { Badge, Card, Skeleton, Stat, TensionBar } from '../components/ui.js';

const STATUS_TONE = {
  open: 'success',
  locked: 'warning',
  settled: 'info',
  cancelled: 'danger',
} as const;

export function PeleaActual(): JSX.Element {
  const t = useT();
  const client = useQueryClient();
  const { data, isLoading, isError, refetch } = useCurrentFight();
  const fight = data?.fight ?? null;
  const [liveMetrics, setLiveMetrics] = useState<FightMetrics | null>(null);

  const onEvent = useCallback((event: StreamEvent) => {
    if (event.type === 'pool.updated') setLiveMetrics(event.metrics);
    if (event.type === 'fight.status') void refetch();
    if (event.type === 'bet.created') void client.invalidateQueries({ queryKey: queryKeys.bets(event.fightId) });
  }, [refetch, client]);

  const onPoll = useCallback(() => {
    void refetch();
  }, [refetch]);

  const { status: streamStatus } = useFightStream({
    fightId: fight?.id ?? null,
    onEvent,
    onPoll,
    enabled: Boolean(fight),
  });

  useEffect(() => {
    if (data?.metrics) setLiveMetrics(data.metrics);
  }, [data?.metrics]);

  if (isLoading) {
    return (
      <div className="grid gap-3">
        <Skeleton height={120} />
        <Skeleton height={220} />
      </div>
    );
  }

  if (isError) {
    return (
      <Card title={t('error.title')}>
        <p className="text-[var(--pit-state-danger)]">{t('error.network')}</p>
      </Card>
    );
  }

  if (!fight) {
    return (
      <div className="pit-card pit-anim-up flex flex-col items-center gap-3 p-10 text-center">
        <span
          aria-hidden
          className="pit-hex pit-anim-float grid h-16 w-16 place-items-center"
          style={{
            background:
              'linear-gradient(160deg, color-mix(in srgb, var(--pit-accent-red) 55%, transparent), color-mix(in srgb, var(--pit-accent-amber) 20%, transparent))',
            boxShadow: '0 0 40px -12px var(--pit-accent-red)',
          }}
        >
          <span className="pit-mono text-[var(--pit-text-lg)] font-[var(--pit-weight-black)] text-white">TP</span>
        </span>
        <p className="pit-title pit-mono text-[var(--pit-text-xl)] font-[var(--pit-weight-black)] uppercase tracking-[var(--pit-tracking-wider)]">
          {t('fight.none')}
        </p>
        <p className="text-[var(--pit-text-sm)] text-[var(--pit-text-muted)]">{t('fight.noneHint')}</p>
      </div>
    );
  }

  const metrics = liveMetrics ?? fight.metrics;
  const currency = fight.rulesSnapshot.currency;

  return (
    <div className="grid gap-3">
      <Card
        title={`${t('fight.current')} · ${fight.title}`}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={streamStatus === 'live' ? 'success' : streamStatus === 'polling' ? 'warning' : 'neutral'}>
              {t(`stream.${streamStatus}`)}
            </Badge>
            <Badge tone={STATUS_TONE[fight.status]} pulse={fight.status === 'open'}>
              {t(`fight.status.${fight.status}`)}
            </Badge>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <CornerSummary
            side="red"
            name={fight.redFighter.name}
            crew={fight.redFighter.crew}
            poolCents={metrics.redPoolCents}
            betCount={metrics.redBetCount}
            oddsBp={metrics.oddsRedBp}
            currency={currency}
          />
          <div className="flex items-center justify-center">
            <span className="pit-mono text-[var(--pit-text-2xl)] font-[var(--pit-weight-black)] text-[var(--pit-text-muted)]">
              {t('fight.vs')}
            </span>
          </div>
          <CornerSummary
            side="blue"
            name={fight.blueFighter.name}
            crew={fight.blueFighter.crew}
            poolCents={metrics.bluePoolCents}
            betCount={metrics.blueBetCount}
            oddsBp={metrics.oddsBlueBp}
            currency={currency}
          />
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="pit-mono text-[var(--pit-text-2xs)] uppercase text-[var(--pit-text-muted)]">
              {t('fight.tension')}
            </span>
            <span className="pit-mono text-[var(--pit-text-2xs)]">{metrics.tensionPct}%</span>
          </div>
          <TensionBar value={metrics.tensionPct} label={t('a11y.poolChart')} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t('fight.pool')} value={formatMoney(metrics.totalPoolCents, currency)} tone="accent" />
          <Stat label={t('fight.netPool')} value={formatMoney(metrics.netPoolCents, currency)} />
          <Stat label={t('fight.rake')} value={formatPercentBp(fight.rulesSnapshot.rakeBp)} />
          <Stat label={t('fight.bets')} value={metrics.betCount} />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-[var(--pit-text-2xs)] text-[var(--pit-text-muted)]">
          <span className="pit-mono">
            {t('fight.lastBet', { time: formatRelative(metrics.lastBetAt) })}
          </span>
          <span className="pit-mono">{t('fight.version', { version: fight.version })}</span>
          <span className="pit-mono">
            {fight.rulesSnapshot.presetId}@{fight.rulesSnapshot.presetSemver}
          </span>
          <span className="pit-mono">{t('fight.oddsEstimated')}</span>
        </div>

        {fight.winner && (
          <p className="pit-mono mt-3 text-[var(--pit-text-sm)] uppercase text-[var(--pit-accent-amber)]">
            {t('fight.winner', { corner: fight.winner === 'red' ? t('fight.red') : t('fight.blue') })}
          </p>
        )}
      </Card>

      <p aria-live="polite" className="sr-only">
        {t('a11y.liveRegionPool')}
      </p>
    </div>
  );
}

function CornerSummary({
  side,
  name,
  crew,
  poolCents,
  betCount,
  oddsBp,
  currency,
}: {
  side: 'red' | 'blue';
  name: string;
  crew: string | null;
  poolCents: number;
  betCount: number;
  oddsBp: number;
  currency: { code: string; symbol: string; decimals: number };
}): JSX.Element {
  const t = useT();
  const color = side === 'red' ? 'var(--pit-corner-red)' : 'var(--pit-corner-blue)';
  return (
    <div className="pit-corner flex flex-col" style={{ '--corner': color } as CSSProperties}>
      <div className="pit-corner-bar" />
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-center justify-between gap-2">
          <p
            className="pit-mono text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)]"
            style={{ color }}
          >
            {side === 'red' ? t('fight.red') : t('fight.blue')}
          </p>
          {crew && (
            <span className="pit-mono truncate text-[var(--pit-text-2xs)] text-[var(--pit-text-muted)]">{crew}</span>
          )}
        </div>
        <p className="break-words text-[var(--pit-text-xl)] font-[var(--pit-weight-black)] uppercase leading-[var(--pit-leading-tight)]">
          {name}
        </p>
        <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
          <Stat label={t('fight.pool')} value={formatMoney(poolCents, currency)} tone={side} />
          <Stat label={t('fight.odds')} value={formatOdds(oddsBp)} />
        </div>
        <p className="pit-mono text-[var(--pit-text-2xs)] text-[var(--pit-text-muted)]">
          {t('fight.bets')}: {betCount}
        </p>
      </div>
    </div>
  );
}
